/**
 * The navigation.
 *
 * # The rule this file exists to hold
 *
 * *"Only expose navigation items that have a meaningful product destination.
 * If a backend capability does not yet exist, do not create fake functional
 * pages merely to populate navigation."*
 *
 * Every item below names the `/v1` operations its page actually calls. That is
 * not decoration — it is the evidence that the item passes the rule, and it is
 * checked: `navigation.test.ts` asserts that every `backedBy` entry is an
 * operation `src/api/operations.ts` exports, so an item cannot be added with a
 * plausible-sounding endpoint that does not exist.
 *
 * # What is deliberately absent, and why
 *
 * **Pull requests, Issues, Runs and Findings as top-level destinations.** Every
 * one of those routes is `/v1/organizations/{org_id}/repositories/{repo_id}/…`
 * and needs a repository id. They are reachable *within* a repository, from
 * the overview (the review requests and assigned issues the dashboard route
 * returns) and from Search, which is how a person finds one across the
 * organization.
 *
 * **An organization list.** No route lists a user's organizations (it would
 * be a query across tenants). The Organization page switches by name instead,
 * through `GET /v1/users/me/organizations/{slug}`, which answers only for
 * members.
 *
 * **Exi Git AI.** Out of scope for this foundation by instruction, and
 * `internal/ai/` is an empty directory.
 */

import type { ComponentType } from 'react';

import * as Icon from '~/components/icons';
import type { IconProps } from '~/components/icons';

export interface NavItem {
  /** The route path. Matches a route in `src/app/router.tsx`. */
  to: string;
  label: string;
  icon: ComponentType<IconProps>;
  /** Shown under the label in the command palette. */
  description: string;
  /**
   * The `src/api/operations.ts` exports this destination calls.
   *
   * Empty is not allowed. An item with no operation behind it is the thing the
   * rule above forbids, and `navigation.test.ts` fails on one.
   */
  backedBy: string[];
}

export const NAVIGATION: NavItem[] = [
  {
    to: '/',
    label: 'Overview',
    icon: Icon.Activity,
    description: 'Your engineering command center',
    backedBy: [
      'getCurrentUser',
      'getOrganization',
      'listFavoriteRepositories',
      'listPersonalActivity',
      'listNotifications',
      'getDashboard',
    ],
  },
  {
    to: '/repositories',
    label: 'Repositories',
    icon: Icon.Repository,
    description: 'Browse, create and clone repositories; pull requests and issues inside them',
    backedBy: [
      'listRepositories',
      'createRepository',
      'listFavoriteRepositories',
      'listWatchedRepositories',
      'getTree',
      'listPullRequests',
      'listIssues',
    ],
  },
  {
    to: '/organization',
    label: 'Organization',
    icon: Icon.Organization,
    description: 'Switch organization, create one, manage its members and its review rules',
    backedBy: [
      'getOrganization',
      'getMyOrganizationBySlug',
      'createOrganization',
      'listOrganizationMembers',
      'addOrganizationMember',
      'listOrganizationAIReviewRules',
      'createOrganizationAIReviewRule',
    ],
  },
  {
    to: '/projects',
    label: 'Projects',
    icon: Icon.Rows,
    description: 'Boards of issues from across the organization',
    backedBy: [
      'listProjects',
      'createProject',
      'getProject',
      'listProjectItems',
      'moveProjectItem',
      'addProjectItem',
    ],
  },
  {
    to: '/search',
    label: 'Search',
    icon: Icon.Search,
    description: 'Find issues, pull requests, repositories and people',
    backedBy: ['searchOrganization'],
  },
  {
    to: '/activity',
    label: 'Activity',
    icon: Icon.Branch,
    description: 'Everything that has happened, from the audit record',
    backedBy: ['listPersonalActivity'],
  },
  {
    to: '/inbox',
    label: 'Inbox',
    icon: Icon.Bell,
    description: 'Notifications addressed to you',
    backedBy: [
      'listNotifications',
      'acknowledgeNotification',
      'markNotificationUnread',
      'acknowledgeAllNotifications',
      'getNotificationUnreadCount',
    ],
  },
  {
    to: '/security',
    label: 'Security',
    icon: Icon.Shield,
    description:
      "The organization's security findings, prioritised from the evidence, and the policy that governs them",
    backedBy: ['getSecurityOverview', 'listOrgSecurityFindings', 'getSecurityPolicy'],
  },
  {
    to: '/automations',
    label: 'Automation',
    icon: Icon.Run,
    description: 'Workflows the platform follows on its own when events happen',
    backedBy: [
      'listAutomationWorkflows',
      'createAutomationWorkflow',
      'getAutomationWorkflow',
      'listAutomationExecutions',
      'getAutomationExecution',
      'getAutomationCatalog',
    ],
  },
  {
    to: '/governance',
    label: 'Governance',
    icon: Icon.Shield,
    description:
      'Policies enforced at merge, release and deployment, exceptions and compliance evidence',
    backedBy: [
      'listPolicies',
      'getGovernanceInsights',
      'listPolicyExceptions',
      'listComplianceReports',
    ],
  },
  {
    to: '/integrations',
    label: 'Integrations',
    icon: Icon.Globe,
    description: "The organization's connections to external systems, their health and their logs",
    backedBy: [
      'listIntegrationProviders',
      'listIntegrations',
      'connectIntegration',
      'getIntegration',
      'listIntegrationDeliveries',
      'listIntegrationReceipts',
    ],
  },
  {
    to: '/observability',
    label: 'Observability',
    icon: Icon.Activity,
    description: 'Services, their objectives and error budgets, metrics and incidents',
    backedBy: [
      'listServices',
      'createService',
      'getService',
      'listServiceSLOs',
      'createServiceSLO',
      'getServiceSLO',
      'queryIntegrationTelemetryRange',
      'listServiceIncidents',
      'listIncidents',
    ],
  },
  {
    to: '/traceability',
    label: 'Traceability',
    icon: Icon.Branch,
    description: 'Commit to deployment lineage: builds, artifacts, releases, environments',
    backedBy: [
      'getTraceCoverage',
      'traceCommit',
      'getDeploymentChanges',
      'listArtifacts',
      'listEnvironments',
      'listReleaseHistory',
      'listDeploymentHistory',
    ],
  },
  {
    to: '/audit',
    label: 'Audit log',
    icon: Icon.Key,
    description: 'Every recorded action, for owners and security managers',
    backedBy: ['listAuditEvents'],
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Icon.Settings,
    description: 'Your account, access tokens and appearance',
    backedBy: ['getCurrentUser', 'listTokens', 'listMFAMethods'],
  },
];
